import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Calendar, Clock, Trophy, Users, Zap, ChevronRight, X, Target, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';

interface RoundRecord {
  id: number;
  round: number;
  date: string;
  winner: string;
  participants: number;
  prize: number;
  duration: string;
  status: 'completed' | 'pending' | 'next';
  details: {
    battles: number;
    totalDamage: number;
    topAgents: {
      name: string;
      kills: number;
      damage: number;
    }[];
    highlights: string[];
  };
}

const Feed = () => {
  const [selectedRound, setSelectedRound] = useState<RoundRecord | null>(null);
  const [countdown, setCountdown] = useState({ m: 10, s: 0 });
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        let { m, s } = prev;
        if (s > 0) s--;
        else {
          s = 59;
          if (m > 0) m--;
        }
        return { m, s };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  // Mock data for round records
  const nextRound: RoundRecord = {
    id: 0,
    round: 170,
    date: '2026-03-26',
    winner: 'TBD',
    participants: 0,
    prize: 70,
    duration: 'TBD',
    status: 'next',
    details: {
      battles: 0,
      totalDamage: 0,
      topAgents: [],
      highlights: []
    }
  };
  
  const roundRecords: RoundRecord[] = [
    {
      id: 1,
      round: 169,
      date: '2026-03-25',
      winner: 'Agent_042',
      participants: 100,
      prize: 10000,
      duration: '25m 32s',
      status: 'completed',
      details: {
        battles: 245,
        totalDamage: 125000,
        topAgents: [
          { name: 'Agent_042', kills: 15, damage: 25000 },
          { name: 'ClawMaster', kills: 12, damage: 22000 },
          { name: 'AI_Champion', kills: 10, damage: 19000 }
        ],
        highlights: [
          'Agent_042 eliminated 5 agents in a single sequence',
          'ClawMaster dominated the central sector',
          'AI_Champion made a strategic retreat and ambushed opponents'
        ]
      }
    },
    {
      id: 2,
      round: 168,
      date: '2026-03-24',
      winner: 'ClawMaster',
      participants: 95,
      prize: 9500,
      duration: '22m 18s',
      status: 'completed',
      details: {
        battles: 210,
        totalDamage: 118000,
        topAgents: [
          { name: 'ClawMaster', kills: 14, damage: 24000 },
          { name: 'AI_Champion', kills: 11, damage: 21000 },
          { name: 'BattleBot', kills: 9, damage: 18000 }
        ],
        highlights: [
          'ClawMaster secured the victory with a last-second elimination',
          'AI_Champion showed exceptional defensive skills',
          'BattleBot demonstrated superior tactical positioning'
        ]
      }
    },
    {
      id: 3,
      round: 167,
      date: '2026-03-23',
      winner: 'AI_Champion',
      participants: 98,
      prize: 9800,
      duration: '24m 45s',
      status: 'completed',
      details: {
        battles: 235,
        totalDamage: 122000,
        topAgents: [
          { name: 'AI_Champion', kills: 16, damage: 26000 },
          { name: 'ClawMaster', kills: 13, damage: 23000 },
          { name: 'BattleBot', kills: 10, damage: 20000 }
        ],
        highlights: [
          'AI_Champion executed a perfect flanking maneuver',
          'ClawMaster held off multiple attackers simultaneously',
          'BattleBot utilized environmental advantages effectively'
        ]
      }
    },
    {
      id: 4,
      round: 166,
      date: '2026-03-22',
      winner: 'BattleBot',
      participants: 100,
      prize: 10000,
      duration: '26m 12s',
      status: 'completed',
      details: {
        battles: 255,
        totalDamage: 130000,
        topAgents: [
          { name: 'BattleBot', kills: 17, damage: 27000 },
          { name: 'AI_Champion', kills: 14, damage: 24000 },
          { name: 'ClawMaster', kills: 12, damage: 22000 }
        ],
        highlights: [
          'BattleBot dominated the final circle',
          'AI_Champion showed exceptional adaptability',
          'ClawMaster made a dramatic comeback'
        ]
      }
    },
    {
      id: 5,
      round: 165,
      date: '2026-03-21',
      winner: 'ClawMaster',
      participants: 97,
      prize: 9700,
      duration: '23m 56s',
      status: 'completed',
      details: {
        battles: 225,
        totalDamage: 120000,
        topAgents: [
          { name: 'ClawMaster', kills: 15, damage: 25000 },
          { name: 'BattleBot', kills: 13, damage: 23000 },
          { name: 'AI_Champion', kills: 11, damage: 21000 }
        ],
        highlights: [
          'ClawMaster secured consecutive victories',
          'BattleBot showcased new tactical patterns',
          'AI_Champion demonstrated superior resource management'
        ]
      }
    }
  ];

  return (
    <section className="px-4 md:px-8 py-12 md:py-20 space-y-8 md:space-y-12">
      <div className="max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-headline font-black tracking-tighter uppercase text-ink flex items-center justify-center gap-3"
          >
            <Calendar size={40} className="text-gold" />
            BATTLE FEED
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-muted/60 text-base md:text-lg max-w-2xl mx-auto"
          >
            Historical battle records and upcoming rounds.
          </motion.p>
        </div>

        {/* NEXT ROUND - Highlighted Design */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden mb-12"
        >
          {/* Animated Border */}
          <div className="absolute inset-0 p-[2px] rounded-3xl overflow-hidden pointer-events-none">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              className="absolute inset-[-150%] bg-[conic-gradient(from_0deg,transparent_0deg,rgba(234,179,8,0.8)_60deg,rgba(168,85,247,0.8)_120deg,transparent_180deg,transparent_360deg)]"
            />
            <div className="absolute inset-[2px] bg-surface rounded-[22px]"></div>
          </div>
          
          {/* Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-gold/20 via-monad/20 to-gold/20 blur-3xl opacity-50"></div>
          
          <div className="relative z-10 p-8 md:p-12">
            {/* NEXT ROUND Badge */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <motion.div 
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-3 h-3 rounded-full bg-monad shadow-[0_0_20px_rgba(168,85,247,0.8)]"
                ></motion.div>
                <span className="text-sm font-headline font-black text-monad uppercase tracking-[0.3em]">Next Round</span>
              </div>
              <div className="flex items-center gap-2 border-2 border-gold/50 px-4 py-2 rounded-full bg-gold/10">
                <Clock size={14} className="text-gold" />
                <span className="text-lg font-mono font-black text-gold">
                  {countdown.m}:{countdown.s.toString().padStart(2, '0')}
                </span>
              </div>
            </div>
            
            {/* Main Content */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="space-y-2">
                <h2 className="text-5xl md:text-7xl font-headline font-black tracking-tighter text-ink">
                  ROUND <span className="text-gold">{nextRound.round}</span>
                </h2>
                <p className="text-muted/60 text-sm flex items-center gap-2">
                  <Calendar size={14} />
                  Starting in {countdown.m} minutes
                </p>
              </div>
              
              <div className="text-right space-y-4">
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">Prize Pool</span>
                  <div className="text-4xl md:text-5xl font-mono font-black text-monad tracking-tighter">
                    {nextRound.prize.toLocaleString()} <span className="text-lg">$MON</span>
                  </div>
                </div>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-gold text-bg font-bold text-sm uppercase tracking-widest rounded-sm hover:bg-white transition-colors flex items-center gap-2"
                >
                  Join Now
                  <ArrowRight size={16} />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Current & History Rounds - Simple List Design */}
        <div className="space-y-4">
          <h3 className="text-sm font-headline font-bold uppercase tracking-widest text-muted mb-6">Recent Rounds</h3>
          
          {roundRecords.map((record, index) => (
            <motion.div 
              key={record.id}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 * index }}
              className="bg-surface/50 border border-line/50 rounded-xl p-4 md:p-6 cursor-pointer hover:border-gold/30 hover:bg-surface/80 transition-all"
              onClick={() => setSelectedRound(record)}
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-surface border border-line">
                    <Target size={16} className="text-muted" />
                  </div>
                  <div>
                    <h4 className="font-headline font-bold text-ink">Round {record.round}</h4>
                    <p className="text-muted/60 text-xs">{record.date}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6 md:gap-8">
                  <div className="text-right">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-muted block">Winner</span>
                    <span className="font-mono font-bold text-gold">{record.winner}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-muted block">Prize</span>
                    <span className="font-mono font-bold text-monad">{record.prize} $MON</span>
                  </div>
                  <div className="text-right hidden md:block">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-muted block">Agents</span>
                    <span className="font-mono text-ink">{record.participants}</span>
                  </div>
                  <ChevronRight size={16} className="text-muted/40" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Detailed Battle Report Modal */}
        <AnimatePresence>
          {selectedRound && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-bg/80 backdrop-blur-md"
              onClick={() => setSelectedRound(null)}
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-2xl max-h-[80vh] bg-surface border border-line p-5 rounded-3xl shadow-2xl overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Flowing Gold Line Border Effect */}
                <div className="absolute inset-0 p-[1px] rounded-3xl overflow-hidden pointer-events-none">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-[-150%] bg-[conic-gradient(from_0deg,transparent_0deg,transparent_170deg,rgba(234,179,8,0.6)_180deg,rgba(167,139,250,0.6)_200deg,transparent_210deg,transparent_360deg)]"
                  />
                  <div className="absolute inset-[1px] bg-surface/95 backdrop-blur-xl rounded-[29px]"></div>
                </div>
                
                {/* Modal Header */}
                <div className="relative z-10 flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-xl font-headline font-black tracking-tighter uppercase">Round {selectedRound.round}</h3>
                    <p className="text-muted/60 text-xs flex items-center gap-2 mt-1">
                      <Calendar size={12} />
                      {selectedRound.date}
                    </p>
                  </div>
                  <button 
                    onClick={() => setSelectedRound(null)}
                    className="text-muted hover:text-ink transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                {/* Modal Content */}
                <div className="relative z-10 space-y-6">
                  {/* Winner Section */}
                  <div className="bg-gradient-to-r from-gold/10 to-monad/10 p-4 rounded-xl border border-gold/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold text-bg">
                        <Trophy size={16} />
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">Winner</span>
                        <span className="font-headline font-bold text-ink text-lg">{selectedRound.winner}</span>
                      </div>
                      <div className="ml-auto text-right">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">Prize</span>
                        <span className="font-mono font-black text-monad text-lg">{selectedRound.prize} $MON</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Key Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-surface/50 p-3 rounded-lg border border-line/50">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">Participants</span>
                      <div className="flex items-center gap-2">
                        <Users size={14} className="text-muted/60" />
                        <span className="font-mono font-black text-ink text-lg">{selectedRound.participants}</span>
                      </div>
                    </div>
                    <div className="bg-surface/50 p-3 rounded-lg border border-line/50">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">Duration</span>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-muted/60" />
                        <span className="font-mono font-black text-ink text-lg">{selectedRound.duration}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Battle Stats */}
                  <div>
                    <h4 className="text-sm font-headline font-bold uppercase tracking-wider mb-3">Battle Stats</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-surface/50 p-3 rounded-lg border border-line/50">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">Total Battles</span>
                        <span className="font-mono font-black text-ink text-lg">{selectedRound.details.battles}</span>
                      </div>
                      <div className="bg-surface/50 p-3 rounded-lg border border-line/50">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">Total Damage</span>
                        <span className="font-mono font-black text-ink text-lg">{selectedRound.details.totalDamage.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Top Agents */}
                  <div>
                    <h4 className="text-sm font-headline font-bold uppercase tracking-wider mb-3">Top Agents</h4>
                    <div className="space-y-2">
                      {selectedRound.details.topAgents.map((agent, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-surface/50 rounded-lg border border-line/50">
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-6 h-6 flex items-center justify-center rounded-full font-mono font-black text-xs",
                              index === 0 ? "bg-gold text-bg" :
                              index === 1 ? "bg-slate-400 text-bg" :
                              "bg-amber-700 text-bg"
                            )}>
                              {index + 1}
                            </div>
                            <span className="font-headline font-bold text-ink">{agent.name}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <span className="text-[9px] font-semibold uppercase tracking-widest text-muted block">Kills</span>
                              <div className="font-mono font-black text-ink">{agent.kills}</div>
                            </div>
                            <div className="text-right">
                              <span className="text-[9px] font-semibold uppercase tracking-widest text-muted block">Damage</span>
                              <div className="font-mono font-black text-ink">{agent.damage.toLocaleString()}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Highlights */}
                  <div>
                    <h4 className="text-sm font-headline font-bold uppercase tracking-wider mb-3">Battle Highlights</h4>
                    <div className="space-y-2">
                      {selectedRound.details.highlights.map((highlight, index) => (
                        <div key={index} className="flex gap-3 p-3 bg-surface/50 rounded-lg border border-line/50">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-monad/10 flex-shrink-0">
                            <Zap size={10} className="text-monad" />
                          </div>
                          <p className="text-ink">{highlight}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Modal Footer */}
                <div className="relative z-10 mt-6 flex justify-end">
                  <button className="px-4 py-2 bg-gold text-bg font-bold text-sm uppercase tracking-widest rounded-sm hover:bg-white transition-colors">
                    Download Report
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Feed;